/**
 * ImageGalleryGroup Component
 * Ragruppa immagini consecutive senza caption in una griglia compatta
 */

import { memo } from 'react';
import { MediaProxy } from './MediaProxy';
import { MessageTime } from './MessageTime';
import { MessageStatus } from './MessageStatus';
import { getSenderColor, formatSenderName } from '@/lib/utils/whatsappHelpers';
import type { Message } from '@/lib/types/whatsappChat';

interface ImageGalleryGroupProps {
  messages: Message[];
  isGroup?: boolean;
  onImageClick?: (mediaUrl: string) => void;
}

/**
 * Layout griglia in base al numero di immagini:
 * 2: 2 colonne
 * 3: 2+1 (prima riga 2 col, seconda riga 1 piena)
 * 4: 2x2
 * 5: 2+3 o 3+2
 * 6: 3x2
 */
function getGridClass(count: number): string {
  if (count === 1) return 'grid-cols-1';
  if (count <= 4) return 'grid-cols-2';
  return 'grid-cols-3';
}

export const ImageGalleryGroup = memo(function ImageGalleryGroup({
  messages,
  isGroup = false,
  onImageClick,
}: ImageGalleryGroupProps) {
  if (messages.length === 0) return null;

  const first = messages[0];
  const last = messages[messages.length - 1];
  const isOutbound = first.direction === 'outbound';
  const showSender = isGroup && !isOutbound && first.senderJid;
  const senderColor = showSender ? getSenderColor(first.senderJid!) : undefined;

  return (
    <div className={`group flex items-end gap-1 max-w-[80%] min-w-0 ${isOutbound ? 'ml-auto' : 'mr-auto'}`}>
      <div className="flex flex-col">
        {showSender && (
          <p className="text-xs font-semibold mb-0.5 truncate" style={{ color: senderColor }}>
            {formatSenderName(first.senderJid!, first.senderName)}
          </p>
        )}
        <div className={`grid ${getGridClass(messages.length)} gap-0.5 rounded-2xl overflow-hidden`}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className="relative cursor-pointer overflow-hidden"
              style={{ aspectRatio: '1', minWidth: 100, maxWidth: 200 }}
              onClick={() => msg.mediaUrl && onImageClick?.(msg.mediaUrl)}
            >
              <MediaProxy
                url={msg.mediaUrl || ''}
                type="image"
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        {/* Footer: timestamp dell'ultimo messaggio + status */}
        <div className={`flex items-center gap-1 mt-0.5 ${isOutbound ? 'justify-end' : ''}`}>
          <MessageTime timestamp={last.timestamp} isOutbound={isOutbound} />
          {isOutbound && (
            <MessageStatus status={last.status} isOutbound={isOutbound} />
          )}
        </div>
      </div>
    </div>
  );
});
